import React, { Component } from 'react';
import CheckCircleIcon from '@atlaskit/icon/glyph/check-circle';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import InfoIcon from '@atlaskit/icon/glyph/info';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import AutoDismissFlag from '@atlassian/jira-flags/src/common/ui/components/ak-auto-dismiss-flag';
import FlagGroup from '@atlassian/jira-flags/src/common/ui/components/group-flag';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type {
	IntlShapeV2 as IntlShape,
	MessageDescriptorV2 as MessageDescriptor,
} from '@atlassian/jira-intl/src/v2/types.tsx';
import {
	type FlagType,
	SUCCESS_TYPE,
	WARNING_TYPE,
	INFO_TYPE,
	ERROR_TYPE,
} from '@atlassian/jira-servicedesk-common/src/model/flags';
import type { Flag as FlagDescriptor } from '../state/flags/types';
import { Display } from './display';

type Props = {
	flags: FlagDescriptor[];
	onDismissed: (arg1: string | number) => void;
	intl: IntlShape;
};

const iconFromType = (flagType: FlagType) => {
	switch (flagType) {
		case SUCCESS_TYPE:
			return (
				<CheckCircleIcon
					size="medium"
					label="Success"
					primaryColor={token('color.icon.success', colors.G200)}
				/>
			);
		case WARNING_TYPE:
			return (
				<WarningIcon
					size="medium"
					label="Warning"
					primaryColor={token('color.icon.warning', colors.Y200)}
				/>
			);
		case INFO_TYPE:
			return (
				<InfoIcon
					size="medium"
					label="Info"
					primaryColor={token('color.icon.discovery', colors.P300)}
				/>
			);
		case ERROR_TYPE:
			return (
				<ErrorIcon
					size="medium"
					label="Error"
					primaryColor={token('color.icon.danger', colors.R200)}
				/>
			);
		default:
			return null;
	}
};

// eslint-disable-next-line jira/react/no-class-components
class Flags extends Component<Props> {
	stringify(input: MessageDescriptor | string): string {
		const {
			intl: { formatMessage },
		} = this.props;
		if (typeof input === 'string') {
			return input;
		}
		return formatMessage(input);
	}

	renderFlag(flag: FlagDescriptor) {
		const { id, title, description, type, actions = [] } = flag;
		// Currently only supports auto-dismissed flags and a small number of
		// props. Extend as needed.
		return (
			<AutoDismissFlag
				id={id}
				key={id}
				title={this.stringify(title)}
				description={description && <Display displayable={description} />}
				icon={iconFromType(type)}
				actions={actions.map(({ onClick, content }) => ({
					onClick,
					content: this.stringify(content),
				}))}
			/>
		);
	}

	render() {
		return (
			<FlagGroup onDismissed={this.props.onDismissed}>
				{this.props.flags.map((flag) => this.renderFlag(flag))}
			</FlagGroup>
		);
	}
}

export default injectIntl(Flags);
